import { useTranslation } from 'react-i18next';
import { isMobileOnly } from 'react-device-detect';
import { Box } from '@mui/material';
import React, {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  SyntheticEvent,
  useMemo,
} from 'react';
import { createGlobalStyle } from 'styled-components';

import {
  Header,
  ModalContainer,
  Tabs,
} from '@core/dashboard/components/PopoverModal';
import { ChatTabs, ThirdPartyChatProvider } from '@core/chat/interfaces';
import { useChatState } from '@core/chat';
import {
  useClientSpecificBroadcastChatVisibility,
  useClientSpecificPrivateChatVisibility,
} from '@hooks/clientSpecificUtils';
import SwipeableDrawer from '@core/dashboard/components/SwipeableDrawer';
import { IChatDrawerView } from './interfaces';
import { useStyles } from './styles';
import { env } from 'env';
import { useSearchUserContext } from '@providers/SearchUserProvider';

const StreamChat = lazy(
  () => import('@core/chat/StreamChat/components/StreamChat'),
);

// SwipeableDrawer doesn't have sx prop implemented
// so we use global style to override it and add 20px margin
// to prevent scrollbar being stuck behind SwipeableDrawer on desktop
const GlobalStyle = createGlobalStyle<{ $addMarginRight: boolean }>`
  .PrivateSwipeArea-root {
    &:nth-child(2) {
      margin-right: ${(props) => (props.$addMarginRight ? '20px' : '0')};
    }
  }
`;

const ChatDrawerView = ({
  isOpenDrawer,
  toggleDrawer,
  isLoading,
  hasScreenMediaStreams,
  isExpanded,
  setIsExpanded,
  autoClose,
}: IChatDrawerView) => {
  const { activeTab, setActiveTab } = useChatState();
  const [isHovered, setIsHovered] = useState(false);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const { t: translate } = useTranslation();
  const classes = useStyles({
    isLoading,
    hasScreenMediaStreams,
    isExpanded,
  });
  const {
    focusOnSearchField,
    onFindTapHeader,
    onBackHeader,
    searchResultNumber,
  } = useSearchUserContext();
  const { activePrivateChannel } = useChatState();
  const { isBroadcastEnabled } = useClientSpecificBroadcastChatVisibility();
  const { isPrivateChatEnabled } = useClientSpecificPrivateChatVisibility();

  useEffect(() => {
    const waitTime = parseFloat(
      env.REACT_APP_CHAT_AUTO_CLOSE_TIMEOUT_SECONDS || '120',
    );

    if (!isHovered && timeoutIdRef.current === null) {
      timeoutIdRef.current = setTimeout(() => {
        autoClose();
        timeoutIdRef.current = null;
      }, waitTime * 1000);
    }

    if (isHovered && timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }

    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
  }, [isHovered, autoClose]);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleChangeTab = (_: SyntheticEvent, value: ChatTabs) => {
    setActiveTab(value);
  };

  const renderChat = () => {
    switch (env.REACT_APP_FEATURE_THIRD_PARTY_CHAT_PROVIDER) {
      case ThirdPartyChatProvider.StreamChat:
        return (
          <Suspense>
            <StreamChat />
          </Suspense>
        );
      default:
        return (
          <Suspense>
            <StreamChat />
          </Suspense>
        );
    }
  };

  const tabs = useMemo(() => {
    const activeTabs = [
      {
        label: translate('chatDrawer.public'),
        value: ChatTabs.Global,
      },
    ];

    if (isPrivateChatEnabled) {
      activeTabs.push({
        label: translate('chatDrawer.private'),
        value: ChatTabs.Private,
      });
    }

    if (isBroadcastEnabled) {
      activeTabs.push({
        label: translate('chatDrawer.broadcast'),
        value: ChatTabs.Broadcast,
      });
    }

    return activeTabs;
  }, [isPrivateChatEnabled, isBroadcastEnabled]);

  const shouldTabsBeHidden =
    isMobileOnly && activeTab === ChatTabs.Private && focusOnSearchField;

  const shouldHeaderAndTabsBeHidden =
    isMobileOnly && activeTab === ChatTabs.Private && !!activePrivateChannel;

  const getHeader = () => {
    if (shouldHeaderAndTabsBeHidden) {
      return null;
    } else if (shouldTabsBeHidden) {
      return (
        <Header
          text={translate(`participantsModal.searchResults`, {
            number: searchResultNumber,
          })}
          onClose={toggleDrawer as () => void}
          isBig
          onBack={onBackHeader}
        />
      );
    } else if (isMobileOnly && activeTab === ChatTabs.Private) {
      return (
        <>
          <Header
            text={translate('chatDrawer.title')}
            onClose={toggleDrawer as () => void}
            isBig
            onFind={onFindTapHeader}
          />
          <Tabs
            active={activeTab}
            onChange={handleChangeTab as () => void}
            tabs={tabs}
            sx={classes.tabs}
          />
        </>
      );
    } else {
      return (
        <>
          <Header
            text={translate('chatDrawer.title')}
            onClose={toggleDrawer as () => void}
            isBig
          />
          <Tabs
            active={activeTab}
            onChange={handleChangeTab as () => void}
            tabs={tabs}
            sx={classes.tabs}
          />
        </>
      );
    }
  };

  return (
    <>
      {/*<GlobalStyle $addMarginRight={isDesktop} />*/}
      <SwipeableDrawer
        position={{
          vertical: isMobileOnly ? 'top' : 'bottom',
          horizontal: 'right',
        }}
        //anchor="right"
        open={isOpenDrawer}
        onClose={toggleDrawer}
        onOpen={toggleDrawer}
        // hideBackdrop
        // disableBackdropTransition
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <ModalContainer
          isScrollable={false}
          header={getHeader()}
          sx={classes.modalContainer}
        >
          <Box
            sx={classes.chatWrapper(
              shouldTabsBeHidden,
              shouldHeaderAndTabsBeHidden,
            )}
          >
            {renderChat()}
            {/*
            
            <Box sx={classes.collapseWrap}>
              <ArrowButton
                onClick={() => setIsExpanded(!isExpanded)}
                forwardIcon={isExpanded}
                sx={classes.collapse}
              />
            </Box>
          </Box>*/}
          </Box>
        </ModalContainer>
      </SwipeableDrawer>
    </>
  );
};

export default ChatDrawerView;
